import React, { useRef, useEffect } from 'react';

interface HighlightedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const HighlightedInput: React.FC<HighlightedInputProps> = ({
  value,
  onChange,
  className,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (inputRef.current && backdropRef.current) {
      backdropRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  };

  useEffect(() => {
    handleScroll();
  }, [value]);

  const renderHighlightedText = (text: string) => {
    if (!text) return '';
    const parts = text.split(/(\{\{[a-zA-Z0-9_]+\}\})/g);
    return parts.map((part, index) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return (
          <span 
            key={index} 
            className="bg-[#F6EFFE] text-[#A655F8] rounded inline font-sans"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className="relative w-full border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent h-11 flex items-center">
      <div 
        ref={backdropRef}
        className="absolute inset-0 px-4 flex items-center pointer-events-none whitespace-nowrap overflow-x-hidden text-sm text-gray-900 border-0 font-sans"
        style={{ color: 'transparent' }}
      >
        <div style={{ color: 'initial' }} className="flex items-center">
          {renderHighlightedText(value)}
        </div>
      </div>
      
      <input
        ref={inputRef}
        value={value}
        onChange={onChange}
        onScroll={handleScroll}
        className="relative w-full h-full px-4 bg-transparent text-transparent caret-gray-900 focus:outline-none text-sm font-sans border-0"
        {...props}
      />
    </div>
  );
};

interface HighlightedTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  minHeight?: string;
}

export const HighlightedTextarea: React.FC<HighlightedTextareaProps> = ({
  value,
  onChange,
  className,
  minHeight = "220px",
  ...props
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (textareaRef.current && backdropRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  useEffect(() => {
    handleScroll();
  }, [value]);

  const renderHighlightedText = (text: string) => {
    if (!text) return '\n';
    const parts = text.split(/(\{\{[a-zA-Z0-9_]+\}\})/g);
    return parts.map((part, index) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return (
          <span 
            key={index} 
            className="bg-[#F6EFFE] text-[#A655F8] rounded inline font-sans"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div 
      className="relative w-full border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent flex flex-col justify-between"
      style={{ minHeight }}
    >
      <div className="relative flex-1" style={{ minHeight: `calc(${minHeight} - 38px)` }}>
        <div 
          ref={backdropRef}
          className="absolute inset-0 p-4 pointer-events-none whitespace-pre-wrap break-words overflow-y-scroll text-sm font-sans leading-6 text-gray-900 border-0"
          style={{ color: 'transparent' }}
        >
          <div style={{ color: 'initial' }} className="whitespace-pre-wrap break-words">
            {renderHighlightedText(value)}
          </div>
        </div>
        
        <textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onScroll={handleScroll}
          className="absolute inset-0 w-full h-full p-4 bg-transparent text-transparent caret-gray-900 focus:outline-none resize-none overflow-y-scroll text-sm font-sans leading-6 border-0"
          {...props}
        />
      </div>
      
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex justify-end text-xs text-gray-400 select-none">
        <span>
          {value.length} characters • {value.trim() === '' ? 0 : value.trim().split(/\s+/).length} words
        </span>
      </div>
    </div>
  );
};
